/**
 * Application route constants — canonical home in the `shared/config` slice.
 *
 * Per the canonical FSD layer pyramid (AD-15) widgets and entities cannot
 * import from `@/app/...`. Routes are app-wide configuration primitives —
 * not UI, not domain logic, not server APIs — so `shared/config` is the
 * correct layer for them.
 *
 * History:
 *   - Hoisted from `app/routes/routes.constants.ts`
 *     (initially as a re-export shim with consumers split between old/new paths)
 *   - Re-export shim removed and ALL consumers swept to this canonical path
 *     In (move-app-shell-to-widgets) — required to satisfy AC1
 *     ("App shell components import only from entities/, features/, or shared/")
 *     after lifting AppSidebar into widgets/.
 *
 * The old `@/app/routes/routes.constants` path no longer exists. All callers
 * MUST import from `@/shared/config/routes`.
 */
export const ROUTES = {
  // Main routes
  HOME: '/',
  NEW_CHAT_SESSION: '/new-session',
  AGENT_DETAIL: (agentId: string) => `/agents/${agentId}`,

  // Knowledge Hub routes
  KNOWLEDGE_HUB: '/knowledge-hub',
  KNOWLEDGE_HUB_KB: '/knowledge-hub/knowledge-bases',
  KNOWLEDGE_HUB_KB_DETAIL: (knowledgeBaseId: string) =>
    `/knowledge-hub/knowledge-bases/${knowledgeBaseId}`,
  KNOWLEDGE_HUB_AUDIENCES: '/knowledge-hub/audiences',
  KNOWLEDGE_HUB_AUDIENCE_DETAIL: (audienceId: string) => `/knowledge-hub/audiences/${audienceId}`,
  KNOWLEDGE_HUB_TEMPLATES: '/knowledge-hub/templates',
  KNOWLEDGE_HUB_TEMPLATE_DETAIL: (templateSlug: string) =>
    `/knowledge-hub/templates/${templateSlug}`,
  KNOWLEDGE_HUB_VISUAL_GUIDELINES: '/knowledge-hub/visual-guidelines',
  KNOWLEDGE_HUB_VISUAL_GUIDELINES_FOLDER: (folderId: string) =>
    `/knowledge-hub/visual-guidelines/${folderId}/guidelines`,
  KNOWLEDGE_HUB_BRIEF_TEMPLATES: '/knowledge-hub/brief-templates',
  KNOWLEDGE_HUB_BRIEF_TEMPLATE_NEW: '/knowledge-hub/brief-templates/new',
  KNOWLEDGE_HUB_BRIEF_TEMPLATE_DETAIL: (templateId: string) =>
    `/knowledge-hub/brief-templates/${templateId}`,
  /** The brand's own photography and footage. Distinct from KNOWLEDGE_HUB_MEDIA, which is
   *  ad-platform connectors and has nothing to do with the media library. */
  KNOWLEDGE_HUB_BRAND_MEDIA: '/knowledge-hub/brand-media-library',
  KNOWLEDGE_HUB_TAXONOMY: '/knowledge-hub/taxonomy',
  /** Reusable field library (tab 2). Static segment — must not be read as a taxonomy id. */
  KNOWLEDGE_HUB_TAXONOMY_CATEGORIES: '/knowledge-hub/taxonomy/categories',
  KNOWLEDGE_HUB_TAXONOMY_FIELDS: '/knowledge-hub/taxonomy/fields',
  /** Shared vocabularies (tab 3). */
  KNOWLEDGE_HUB_TAXONOMY_LISTS: '/knowledge-hub/taxonomy/lists',
  KNOWLEDGE_HUB_TAXONOMY_LIST_DETAIL: (listId: string) => `/knowledge-hub/taxonomy/lists/${listId}`,
  KNOWLEDGE_HUB_TAXONOMY_DETAIL: (taxonomyId: string) => `/knowledge-hub/taxonomy/${taxonomyId}`,
  KNOWLEDGE_HUB_MEDIA: '/knowledge-hub/media',
  KNOWLEDGE_HUB_MEDIA_DETAIL: (platform: string) => `/knowledge-hub/media/${platform}`,
  KNOWLEDGE_HUB_MEDIA_GUIDELINES: '/knowledge-hub/media-guidelines',

  // Legacy Knowledge Base routes (for backwards compatibility)
  KNOWLEDGE_BASES: '/knowledge-bases',
  KNOWLEDGE_BASE_DETAIL: (knowledgeBaseId: string) => `/knowledge-bases/${knowledgeBaseId}`,

  // Projects routes
  PROJECTS: '/projects',
  PROJECT_DETAIL: (platformProjectId: string) => `/projects/${platformProjectId}`,
  PROJECT_NEW: '/projects/new',
  PROJECT_OVERVIEW: (platformProjectId: string) => `/projects/${platformProjectId}/overview`,
  PROJECT_BRIEFING: (platformProjectId: string) => `/projects/${platformProjectId}/briefing`,
  PROJECT_BRIEF_DETAIL: (platformProjectId: string, briefId: string) =>
    `/projects/${platformProjectId}/briefing/${briefId}`,
  PROJECT_LIBRARY: (platformProjectId: string) => `/projects/${platformProjectId}/library`,
  PROJECT_DELIVERY: (platformProjectId: string) => `/projects/${platformProjectId}/delivery`,
  PROJECT_DELIVERY_JOB: (platformProjectId: string, jobId: string) =>
    `/projects/${platformProjectId}/delivery/jobs/${jobId}`,
  PROJECT_DASHBOARD: (platformProjectId: string) => `/projects/${platformProjectId}/dashboard`,
  PROJECT_PERFORMANCE: (platformProjectId: string) => `/projects/${platformProjectId}/performance`,
  PROJECT_PERFORMANCE_PACING: (platformProjectId: string) =>
    `/projects/${platformProjectId}/performance/pacing`,
  PROJECT_PERFORMANCE_CREATIVE: (platformProjectId: string) =>
    `/projects/${platformProjectId}/performance/creative`,
  PROJECT_PERFORMANCE_AUDIENCE: (platformProjectId: string) =>
    `/projects/${platformProjectId}/performance/audience`,
  PROJECT_PERFORMANCE_CAMPAIGNS: (platformProjectId: string) =>
    `/projects/${platformProjectId}/performance/campaigns`,
  PROJECT_FOLDERS: (platformProjectId: string) => `/projects/${platformProjectId}/folders`,
  PROJECT_FOLDER_DETAIL: (platformProjectId: string, folderId: string) =>
    `/projects/${platformProjectId}/folders/${folderId}`,
  PROJECT_FILE_DETAIL: (platformProjectId: string, folderId: string, fileId: string) =>
    `/projects/${platformProjectId}/folders/${folderId}/files/${fileId}`,
  PROJECT_FILE_COMPARE: (platformProjectId: string, folderId: string, fileId: string) =>
    `/projects/${platformProjectId}/folders/${folderId}/files/${fileId}/compare`,

  // Content Studio routes
  CONTENT_STUDIO: '/content-studio',
  CONTENT_STUDIO_SYSTEM: (contentBoardId: string) => `/content-studio/${contentBoardId}`,

  // Fabric routes
  FABRIC: '/fabric',

  // Reels Creator routes
  REELS_CREATOR: '/reels-creator',
  REELS_CREATOR_NEW: '/reels-creator/new',
  REELS_CREATOR_SESSION: (sessionId: string, step: string) => `/reels-creator/${sessionId}/${step}`,
  REELS_CREATOR_BRANDS: '/reels-creator/brands',
  /** One brand's kit, opened on one of its sections. */
  REELS_CREATOR_BRAND_SECTION: (brandId: string, section: string) =>
    `/reels-creator/brands?brand=${encodeURIComponent(brandId)}&section=${section}`,
  /** The creative harness's old address: it redirects into the brand kit. */
  REELS_CREATOR_HARNESS: '/reels-creator/creative-harness',
  REELS_CREATOR_SETTINGS: '/reels-creator/settings',

  // Trackers routes
  TRACKERS: '/trackers',
  TRACKER_DETAIL: (trackerId: string) => `/trackers/${trackerId}`,

  // Sessions routes
  SESSIONS: '/sessions',
  CHAT_SESSION: (chatId: string) => `/sessions/${chatId}`,

  // My MCPs (user-level MCP installs)
  MY_MCPS: '/my-mcps',

  // Usage analytics
  ANALYTICS: '/analytics',

  // IQ routes
  IQ: '/iq',
  IQ_NEW_ANALYSIS: '/iq/new-analysis',
  IQ_ANALYSIS: (analysisId: string) => `/iq/${analysisId}`,
  IQ_ANALYSIS_SECTION: (analysisId: string, section: string) => `/iq/${analysisId}/${section}`,
  IQ_AUDIENCE_PERSONA_DETAIL: (analysisId: string, personaId: string) =>
    `/iq/${analysisId}/audience-profiles/${personaId}`,

  // Tools routes
  TOOLS_HUB: '/tools',
  TOOLS_PLAN: '/tools/plan',
  TOOLS_CREATE: '/tools/create',
  TOOLS_SCALE: '/tools/scale',
  TOOLS_DELIVER: '/tools/deliver',
  TOOLS_HUB_CATEGORY: (category: string) => `/tools/${category}`,
  TOOLS_FOCUS_GROUPS: '/tools/focus-groups',
  TOOLS_FOCUS_GROUP_REPORT: (focusGroupId: string) => `/tools/focus-groups/${focusGroupId}`,
  TOOLS_OPPORTUNITIES: '/tools/opportunities-room',
  DOWNLOAD_DOCUMENT: (documentType: string, id: string) => `/download/${documentType}/${id}`,
  TOOLS_CONTENT_TEST: '/tools/content-test',
  TOOLS_CONTENT_TEST_NEW: '/tools/content-test/new',
  TOOLS_CONTENT_TEST_DETAIL: (id: string) => `/tools/content-test/${id}`,
  TOOLS_FOCUS_GROUPS_NEW: '/tools/focus-groups/new',
  TOOLS_NACB_INTAKE: '/tools/nacb-intake',
  TOOLS_TAXONOMY_GENERATION: '/tools/taxonomy-generation',
  TOOLS_TAXONOMY_GENERATION_DETAIL: (datasetId: string) =>
    `/tools/taxonomy-generation/${datasetId}`,

  // Admin routes
  ADMIN: '/admin',
  ADMIN_MODELS: '/admin/models',
  ADMIN_ETL_CONFIG: '/admin/etl',
  ADMIN_AD_FORMATS: '/admin/ad-formats',
  ADMIN_TOOLS: '/admin/tools',
  ADMIN_MCP_CATALOG: '/admin/mcp-catalog',
  ADMIN_CUSTOMIZATION: '/admin/customization',
  ADMIN_CONNECTED_APPS: '/admin/connected-apps',
  ADMIN_EXTERNAL_TOOLS_AGENT_CONFIG: '/admin/tools/external-tools-agent/config',
  ADMIN_AUDIENCE_AGENT_CONFIG: '/admin/tools/audience-flow-agent/config',
  ADMIN_AGENT_CONFIG: (agentId: string) => `/admin/tools/${agentId}/config`,
  ADMIN_TOOL_CONFIG: (agentId: string, toolId: string) =>
    `/admin/tools/${agentId}/configure/${toolId}`,
  ADMIN_CREATE_AGENT: '/admin/tools/new',
  ADMIN_WIDGET: (widgetId: string) => `/admin/widgets/${widgetId}`,
  ADMIN_EDIT_AGENT: (agentId: string) => `/admin/tools/${agentId}/edit`,
} as const;

/* eslint-disable i18next/no-literal-string -- route path segments */
export const ROUTE_PATTERNS = {
  NEW_CHAT_SESSION: 'new-session',
  AGENT_DETAIL: 'agents/:agentId',
  // Knowledge Hub patterns
  KNOWLEDGE_HUB: 'knowledge-hub',
  KNOWLEDGE_HUB_KB: 'knowledge-bases',
  KNOWLEDGE_HUB_KB_DETAIL: ':knowledgeBaseId',
  KNOWLEDGE_HUB_AUDIENCES: 'audiences',
  KNOWLEDGE_HUB_AUDIENCE_DETAIL: ':audienceId',
  KNOWLEDGE_HUB_TEMPLATES: 'templates',
  KNOWLEDGE_HUB_TEMPLATE_DETAIL: ':templateSlug',
  KNOWLEDGE_HUB_VISUAL_GUIDELINES: 'visual-guidelines',
  KNOWLEDGE_HUB_VISUAL_GUIDELINES_FOLDER: ':folderId/guidelines',
  KNOWLEDGE_HUB_BRIEF_TEMPLATES: 'brief-templates',
  KNOWLEDGE_HUB_BRIEF_TEMPLATE_NEW: 'brief-templates/new',
  KNOWLEDGE_HUB_BRIEF_TEMPLATE_DETAIL: 'brief-templates/:templateId',
  KNOWLEDGE_HUB_BRAND_MEDIA: 'brand-media-library',
  KNOWLEDGE_HUB_TAXONOMY: 'taxonomy',
  // Static segments are declared before the dynamic one so `taxonomy/fields`
  // is never matched as a taxonomy id.
  KNOWLEDGE_HUB_TAXONOMY_CATEGORIES: 'taxonomy/categories',
  KNOWLEDGE_HUB_TAXONOMY_FIELDS: 'taxonomy/fields',
  KNOWLEDGE_HUB_TAXONOMY_LISTS: 'taxonomy/lists',
  KNOWLEDGE_HUB_TAXONOMY_DETAIL: 'taxonomy/:taxonomyId',
  KNOWLEDGE_HUB_MEDIA: 'media',
  KNOWLEDGE_HUB_MEDIA_DETAIL: ':platform',
  KNOWLEDGE_HUB_MEDIA_GUIDELINES: 'media-guidelines',
  // Legacy patterns (for backwards compatibility)
  KNOWLEDGE_BASES: 'knowledge-bases',
  KNOWLEDGE_BASE_DETAIL: ':knowledgeBaseId',
  // Projects patterns
  PROJECTS: 'projects',
  PROJECT_DETAIL: ':platformProjectId',
  NEW_PROJECT: 'new',
  PROJECT_SECTION_OVERVIEW: 'overview',
  PROJECT_SECTION_BRIEFING: 'briefing',
  PROJECT_SECTION_LIBRARY: 'library',
  PROJECT_SECTION_APPROVAL_CHAINS: 'workflows',
  PROJECT_SECTION_FOLDERS: 'folders',
  PROJECT_SECTION_DELIVERY: 'delivery',
  PROJECT_DELIVERY_JOB: 'jobs/:jobId',
  PROJECT_FOLDER_DETAIL: ':folderId',
  PROJECT_FILE_DETAIL: 'files/:fileId',
  PROJECT_FILE_COMPARE: 'files/:fileId/compare',
  PROJECT_BRIEF_DETAIL: ':briefId',
  PROJECT_SECTION_DASHBOARD: 'dashboard',
  PROJECT_SECTION_PERFORMANCE: 'performance',
  PROJECT_PERFORMANCE_PACING: 'pacing',
  PROJECT_PERFORMANCE_CREATIVE: 'creative',
  PROJECT_PERFORMANCE_AUDIENCE: 'audience',
  PROJECT_PERFORMANCE_CAMPAIGNS: 'campaigns',
  PROJECT_SECTION_DETAILS: 'details',
  // Content Studio patterns
  CONTENT_STUDIO: 'content-studio',
  CONTENT_STUDIO_SYSTEM: ':contentBoardId',
  // Reels Creator patterns
  REELS_CREATOR: 'reels-creator',
  REELS_CREATOR_NEW: 'new',
  REELS_CREATOR_SESSION: ':sessionId/:step',
  REELS_CREATOR_BRANDS: 'brands',
  REELS_CREATOR_HARNESS: 'creative-harness',
  REELS_CREATOR_SETTINGS: 'settings',
  // Trackers patterns
  TRACKERS: 'trackers',
  TRACKER_DETAIL: ':trackerId',
  SESSIONS: 'sessions',
  CHAT_SESSION: 'sessions/:chatId',
  MY_MCPS: 'my-mcps',
  ANALYTICS: 'analytics',
  // IQ patterns
  IQ: 'iq',
  IQ_NEW_ANALYSIS: 'new-analysis',
  IQ_ANALYSIS: ':analysisId',
  IQ_ANALYSIS_SECTION: ':section',
  IQ_SECTION_EXECUTIVE_SNAPSHOT: 'executive-snapshot',
  IQ_SECTION_BUSINESS_CHALLENGE: 'business-challenge',
  IQ_SECTION_CULTURE_CONTEXT: 'culture-context',
  IQ_SECTION_BRAND_XRAY: 'brand-xray',
  IQ_SECTION_CATEGORY_COMPETITION: 'category-competition',
  IQ_SECTION_BUSINESS_OPPORTUNITIES: 'business-opportunities',
  IQ_SECTION_AUDIENCE_PROFILES: 'audience-profiles',
  IQ_SECTION_MARKETING_PERFORMANCE: 'marketing-performance',
  IQ_SECTION_MEDIA_LANDSCAPE: 'media-landscape',
  IQ_SECTION_CREATIVE_LANDSCAPE: 'creative-landscape',
  IQ_AUDIENCE_PERSONA_DETAIL: ':personaId',
  // Tools patterns
  TOOLS: 'tools',
  TOOLS_PLAN: 'plan',
  TOOLS_CREATE: 'create',
  TOOLS_SCALE: 'scale',
  TOOLS_DELIVER: 'deliver',
  TOOLS_HUB_CATEGORY: ':category',
  TOOLS_FOCUS_GROUPS: 'focus-groups',
  TOOLS_FOCUS_GROUP_DETAIL: 'focus-groups/:focusGroupId',
  TOOLS_NEW_FOCUS_GROUP: 'new-focus-group',
  TOOLS_OPPORTUNITIES: 'opportunities-room',
  DOWNLOAD_DOCUMENT: '/download/:documentType/:id',
  TOOLS_CONTENT_TEST: 'content-test',
  TOOLS_CONTENT_TEST_NEW: 'content-test/new',
  TOOLS_CONTENT_TEST_DETAIL: 'content-test/:contentTestId',
  TOOLS_FOCUS_GROUPS_NEW: 'focus-groups/new',
  TOOLS_NACB_INTAKE: 'nacb-intake',
  TOOLS_TAXONOMY_GENERATION: 'taxonomy-generation',
  TOOLS_TAXONOMY_GENERATION_DETAIL: 'taxonomy-generation/:datasetId',

  // Admin patterns
  ADMIN: 'admin',
  ADMIN_MODELS: 'models',
  ADMIN_ETL_CONFIG: 'etl',
  ADMIN_AD_FORMATS: 'ad-formats',
  ADMIN_TOOLS: 'tools',
  ADMIN_CUSTOMIZATION: 'customization',
  ADMIN_MCP_CATALOG: 'mcp-catalog',
  ADMIN_CONNECTED_APPS: 'connected-apps',
  ADMIN_EXTERNAL_TOOLS_AGENT_CONFIG: 'tools/external-tools-agent/config',
  ADMIN_AUDIENCE_AGENT_CONFIG: 'tools/audience-flow-agent/config',
  ADMIN_AGENT_CONFIG: 'tools/:agentId/config',
  ADMIN_TOOL_CONFIG: 'tools/:agentId/configure/:toolId',
  ADMIN_CREATE_AGENT: 'tools/new',
  ADMIN_WIDGET: 'widgets/:widgetId',
  ADMIN_EDIT_AGENT: 'tools/:agentId/edit',
} as const;
/* eslint-enable i18next/no-literal-string */
